import React, { createContext, useState, useCallback, useEffect } from "react";
import { api } from "../services/api";
import { jwtDecode } from "jwt-decode";
import { useToast } from "@chakra-ui/react";
import { disconnectSocket, getSocket } from '@/services/webSocketEvolution';

interface User {
  id: string;
  uuid?: string;
  email: string;
  name: string;
  avatar_url: string;
  role?: string;
  permissions?: { modules?: string[] };
}

interface AuthState {
  token: string;
  user: User;
}

interface SignInCredentials {
  email: string;
  senha: string;
}

interface AuthContextData {
  user: User | null;
  isAuthenticated: boolean;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): void;
  logout(): void;
  getToken(): Promise<string>;
}

export const AuthContext = createContext<AuthContextData>({} as AuthContextData);

// Hook para usar o contexto de autenticação
export const useAuth = (): AuthContextData => {
  const context = React.useContext(AuthContext);
  
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const toast = useToast();
  const [data, setData] = useState<AuthState>(() => {
    try {
      const token = localStorage.getItem('@Piloto:token');
      const userStr = localStorage.getItem('@Piloto:user');

      if (token && userStr) {
        const user = JSON.parse(userStr);
        api.defaults.headers.authorization = `Bearer ${token}`;
        return { token, user };
      }
    } catch (error) {
      // Se houver erro ao parsear o JSON ou qualquer outro erro,
      // limpa o localStorage e retorna o estado inicial
      localStorage.removeItem('@Piloto:token');
      localStorage.removeItem('@Piloto:user');
    }

    return { token: '', user: {} as User };
  });

  const checkTokenExpiration = useCallback(() => {
    const token = data.token;
    if (!token) return false;

    try {
      const decodedToken = jwtDecode(token);
      if (!decodedToken || typeof decodedToken.exp === "undefined")
        return false;

      // Verifica se o token expira
      const expirationTime = decodedToken.exp * 1000; // Converter para millisegundos
      const currentTime = Date.now();
      const timeUntilExpiry = expirationTime - currentTime;

      if (timeUntilExpiry <= 0) {
        toast({
          title: "Sessão expirada",
          description: "Sua sessão expirou. Por favor, faça login novamente.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
        signOut();
        return true;
      }
      // Se estiver próximo de expirar (menos de 5 minutos), mostrar alerta
      if (timeUntilExpiry <= 5 * 60 * 1000) {
        toast({
          title: "Sessão expirando",
          description:
            "Sua sessão irá expirar em breve. Por favor, faça login novamente para continuar.",
          status: "warning",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      }

      return false;
    } catch (error) {
      console.error("Erro ao decodificar token:", error);
      return false;
    }
  }, [data.token, toast]);

  // Revalida quem é o usuário contra o SERVIDOR ao abrir o app.
  //
  // O `user` do localStorage é um retrato do momento do login e nunca era atualizado:
  // mudar `role` ou `permissions` de alguém no banco não tinha efeito até a pessoa
  // sair e entrar de novo — e ninguém adivinha que precisa. Pior: sessão antiga, de
  // antes de o payload ganhar `role`, fazia um ADMIN ser tratado como usuário comum e
  // ver a biblioteca de módulos cortada.
  //
  // Falha de rede não desloga: mantém o retrato antigo, que é melhor que expulsar
  // alguém do app porque a API piscou.
  useEffect(() => {
    if (!data.token) return;
    let vivo = true;
    api.get('api/auth/me')
      .then(({ data: r }: { data: any }) => {
        const u = r?.data?.user ?? r?.user;
        if (!vivo || !u) return;
        localStorage.setItem('@Piloto:user', JSON.stringify(u));
        setData((d) => ({ ...d, user: u }));
      })
      .catch(() => { /* offline ou API fora: segue com o que está guardado */ });
    return () => { vivo = false; };
  }, [data.token]);

  // Efeito para verificar a expiração do token periodicamente
  useEffect(() => {
    if (!data.token) return;

    const checkInterval = setInterval(() => {
      checkTokenExpiration();
    }, 60000); // Verifica a cada minuto

    return () => clearInterval(checkInterval);
  }, [data.token, checkTokenExpiration]);

  const signIn = useCallback(async ({ email, senha }: SignInCredentials) => {
    const response = await api.post('api/auth/login', {
      email,
      senha,
    });

    // Os dados estão dentro de response.data.data
    const { token, user } = response.data.data;

    localStorage.setItem('@Piloto:token', token);
    localStorage.setItem('@Piloto:user', JSON.stringify(user));

    api.defaults.headers.authorization = `Bearer ${token}`;

    // Cria conexão com o WebSocket
    getSocket(user.uuid);

    setData({ token, user });
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('@Piloto:token');
    localStorage.removeItem('@Piloto:user');

    disconnectSocket();
    setData({ token: '', user: {} as User });
  }, []);

  const getToken = useCallback(async () => {
    return data.token;
  }, [data.token]);

  return (
    <AuthContext.Provider
      value={{
        user: data.token ? data.user : null,
        isAuthenticated: !!data.token,
        signIn,
        signOut,
        logout: signOut,
        getToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
